import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle, AlertTriangle } from 'lucide-react';

interface EmailVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  isGoogleSignup?: boolean;
}

export const EmailVerificationDialog: React.FC<EmailVerificationDialogProps> = ({
  isOpen,
  onClose,
  email,
  isGoogleSignup = false
}) => {
  if (isGoogleSignup) {
    return null; // Não mostrar para cadastro via Google
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent 
        className="sm:max-w-md max-w-[95vw] bg-[#1A2333] border-[#222A35] text-white mx-4"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 sm:h-16 sm:w-16 items-center justify-center rounded-full bg-[#00B6F0]/10">
            <Mail className="h-6 w-6 sm:h-8 sm:w-8 text-[#00B6F0]" />
          </div>
          <DialogTitle className="text-lg sm:text-xl font-semibold text-white">
            Verificação de Email Necessária
          </DialogTitle>
          <DialogDescription className="text-gray-300 text-sm sm:text-base">
            Sua conta foi criada com sucesso! Para continuar, você precisa verificar seu email.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Email enviado para */}
          <div className="bg-[#0F1419] p-3 sm:p-4 rounded-lg border border-[#222A35]">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
              <span className="font-medium text-green-400 text-sm sm:text-base">Email enviado para:</span>
            </div>
            <p className="text-[#00B6F0] font-mono text-xs sm:text-sm break-all">{email}</p>
          </div>

          {/* Instruções */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-[#00B6F0] text-black rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">
                1
              </div>
              <div>
                <p className="font-medium text-white text-sm sm:text-base">Acesse seu email</p>
                <p className="text-xs sm:text-sm text-gray-400">Abra o aplicativo ou site do seu provedor de email</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-[#00B6F0] text-black rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">
                2
              </div>
              <div>
                <p className="font-medium text-white text-sm sm:text-base">Procure o email de verificação</p>
                <p className="text-xs sm:text-sm text-gray-400">Verifique tanto a <strong>caixa de entrada</strong> quanto a pasta <strong>SPAM/Lixo eletrônico</strong></p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-[#00B6F0] text-black rounded-full flex items-center justify-center text-xs sm:text-sm font-bold">
                3
              </div>
              <div>
                <p className="font-medium text-white text-sm sm:text-base">Clique no link de confirmação</p>
                <p className="text-xs sm:text-sm text-gray-400">Isso ativará sua conta e permitirá o acesso</p>
              </div>
            </div>
          </div>

          {/* Aviso importante */}
          <div className="bg-amber-500/10 border border-amber-500/20 p-3 sm:p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500" />
              <span className="font-medium text-amber-400 text-sm sm:text-base">Importante!</span>
            </div>
            <p className="text-xs sm:text-sm text-amber-100">
              Se você não encontrar o email na caixa de entrada, <strong>verifique a pasta SPAM</strong>. 
              Emails automáticos às vezes são filtrados incorretamente.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-6">
          <Button 
            onClick={onClose}
            className="w-full bg-[#00B6F0] hover:bg-[#0095c7] text-black font-medium text-sm sm:text-base py-2.5"
          >
            Entendi, vou verificar meu email
          </Button>
        </div>
        
        <p className="text-xs text-gray-500 text-center mt-4">
          Após verificar seu email, você poderá fazer login normalmente
        </p>
      </DialogContent>
    </Dialog>
  );
}; 